import { ecsClient, getGlobalSessionId } from './index';
import { ECS_BASE_API_URL } from '@/constants/Configs';

export type ChatMessageType = 'text' | 'calling' | 'call_result' | 'call_audio' | 'action';
export type CallResult = 'success' | 'failed';
export type ChatAction = 'link_email' | 'confirm_call';

export interface CallConfirmationData {
  merchantPhone: string;
  merchantName: string;
  productName: string;
  amount?: number;
}

export type ChatRole = 'user' | 'assistant';

export interface ChatResponseMessage {
  type: ChatMessageType;
  content: string;
  callingPhone?: string;
  callResult?: CallResult;
  refundAmount?: number;
  action?: ChatAction;
  callConfirmation?: CallConfirmationData;
  role?: ChatRole;
}

export interface CreateChatSessionResponse {
  sessionId: string;
}

export interface SendMessageResponse {
  sessionId: string;
  response: ChatResponseMessage;
}

export interface GetHistoryResponse {
  sessionId: string;
  messages: ChatResponseMessage[];
}

export interface ChatSessionListItem {
  sessionId: string;
  status: 'active' | 'completed' | 'failed';
  createdAt: number;
  updatedAt: number;
}

export interface ListSessionsResponse {
  sessions: ChatSessionListItem[];
}

export type CallStatus = 'idle' | 'calling' | 'completed' | 'failed';

export interface GetCallStatusResponse {
  status: CallStatus;
  phoneNumber?: string;
  result?: CallResult;
  refundAmount?: number;
  message?: string;
  audioUrl?: string;
  audioDuration?: number;
}

class ChatService {
  /**
   * Create a new chat session
   */
  async createSession(): Promise<CreateChatSessionResponse> {
    const sessionId = getGlobalSessionId();
    if (!sessionId) {
      throw new Error('No session - user not authenticated');
    }

    const response = await ecsClient.post<CreateChatSessionResponse>(
      '/chat/new',
      { sessionId },
    );
    return response.data;
  }

  /**
   * Send a message and get AI response
   */
  async sendMessage(
    chatSessionId: string,
    message: string,
  ): Promise<SendMessageResponse> {
    const sessionId = getGlobalSessionId();
    if (!sessionId) {
      throw new Error('No session - user not authenticated');
    }

    const response = await ecsClient.post<SendMessageResponse>(
      '/chat/message',
      {
        sessionId,
        chatSessionId,
        message,
      },
    );
    return response.data;
  }

  /**
   * Get chat history for a session
   */
  async getHistory(chatSessionId: string): Promise<GetHistoryResponse> {
    const sessionId = getGlobalSessionId();
    if (!sessionId) {
      throw new Error('No session - user not authenticated');
    }

    const response = await ecsClient.get<GetHistoryResponse>('/chat/history', {
      params: {
        sessionId,
        chatSessionId,
      },
    });
    return response.data;
  }

  /**
   * List all chat sessions for user
   */
  async listSessions(): Promise<ListSessionsResponse> {
    const sessionId = getGlobalSessionId();
    if (!sessionId) {
      throw new Error('No session - user not authenticated');
    }

    const response = await ecsClient.get<ListSessionsResponse>(
      '/chat/sessions',
      {
        params: { sessionId },
      },
    );
    return response.data;
  }

  /**
   * Get call status for a chat session
   */
  async getCallStatus(chatSessionId: string): Promise<GetCallStatusResponse> {
    const sessionId = getGlobalSessionId();
    if (!sessionId) {
      throw new Error('No session - user not authenticated');
    }

    const response = await ecsClient.get<GetCallStatusResponse>(
      '/chat/call-status',
      {
        params: {
          sessionId,
          chatSessionId,
        },
      },
    );

    // Convert relative audio URL to absolute URL
    const data = response.data;
    if (data.audioUrl && data.audioUrl.startsWith('/')) {
      const originalUrl = data.audioUrl;
      data.audioUrl = `${ECS_BASE_API_URL}${data.audioUrl}`;
      console.log('[ChatService] Converted audio URL:', originalUrl, '->', data.audioUrl);
      console.log('[ChatService] ECS_BASE_API_URL:', ECS_BASE_API_URL);
    }

    return data;
  }
}

export const chatService = new ChatService();
